import React, {
    useState, useEffect, useMemo, useRef, useCallback,
    useTransition, memo,
} from "react";
import {
    Box, Typography, Button, FormControl, InputLabel,
    Select, MenuItem, Paper, CircularProgress, useMediaQuery, useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
    ResponsiveContainer, Legend, LineChart, Line,
    AreaChart, Area, ReferenceLine,
} from "recharts";
import axios from "axios";
import { useTranslation } from "react-i18next";
import DownloadIcon from "@mui/icons-material/Download";
import html2canvas from "html2canvas";
import { useAuth } from "../context/AuthContext";
import { useParams } from "react-router-dom";
import {
    normalizeActivity,
    getActivityLabel,
    getActivityColor,
    getAllCategories,
} from "../utils/activityNormalizer";

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);

const POZO_FECHAS_CORTE = {
    "TS-03": "2026-04-15",  // 15 de abril de 2026
    "TS-03i": "2026-05-17",  // 17 de mayo de 2026
    "TS-03B": "2026-07-08",  // 8 de julio de 2026
    "TS-03C": "2026-07-14",  // 14 de julio de 2026
    "TS-03D": "2026-09-06",  // 6 de septiembre de 2026
    "TS-04": "2026-10-19",  // 1 de octubre de 2026
};

const DESIGN = {
    blue: "#1565C0",
    blueBright: "#2979FF",
    accent: "#00E5FF",
    accentSoft: "rgba(0,229,255,0.12)",
    surface: "#ffffff",
    surfaceAlt: "#f4f6fb",
    border: "rgba(21,101,192,0.13)",
    borderHover: "rgba(41,121,255,0.35)",
    text: "#0d1b3e",
    textMid: "#3a4a6b",
    textSoft: "#7a8fb5",
    shadow: "0 4px 24px rgba(10,15,46,0.10)",
    shadowHover: "0 12px 40px rgba(21,101,192,0.18)",
    radius: "16px",
    radiusSm: "10px",
};

const TOOLTIP_STYLE = {
    contentStyle: {
        fontSize: 16,
        borderRadius: DESIGN.radiusSm,
        border: `1px solid ${DESIGN.border}`,
        boxShadow: DESIGN.shadow,
        color: DESIGN.text,
    },
};
const CustomTooltipActiv = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const filtrados = payload.filter(p => p.value && p.value > 0);
    if (!filtrados.length) return null;
    return (
        <Box sx={{
            bgcolor: "white", border: `1px solid ${DESIGN.border}`,
            borderRadius: DESIGN.radiusSm, boxShadow: DESIGN.shadow,
            p: "10px 14px", minWidth: 180,
        }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: DESIGN.text, mb: 1 }}>
                {label}
            </Typography>
            {filtrados.map((p, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.4 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: p.fill, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 12, color: DESIGN.textMid, flex: 1 }}>{p.name}</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: DESIGN.text }}>
                        {p.value.toFixed(2)} h
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};
const parseFechaGlobal = (fechaStr) => {
    if (!fechaStr) return null;
    const f = dayjs(fechaStr, ["D/M/YYYY", "DD/MM/YYYY", "D/MM/YYYY", "DD/M/YYYY", "YYYY-MM-DD"], true);
    return f.isValid() ? f.format("YYYY-MM-DD") : null;
};

const parseHsPerf = (raw) => {
    if (!raw) return 0;
    if (typeof raw === "string" && raw.includes(":")) {
        const parts = raw.split(":").map(Number);
        return (parts[0] || 0) + (parts[1] || 0) / 60 + (parts[2] || 0) / 3600;
    }
    return parseFloat(raw) || 0;
};

const formatHoras = (decimal) => {
    const h = Math.floor(decimal);
    const m = Math.round((decimal - h) * 60);
    return `${h}:${String(m).padStart(2, "0")}`;
};

const tickFmt = (v) => {
    const d = dayjs(v, "DD/MM/YYYY", true);
    return d.isValid() ? d.format("DD/MM") : v;
};

const toLang = (i18nLang) => {
    if (!i18nLang) return "es";
    const l = i18nLang.split("-")[0].toLowerCase();
    if (l === "pt") return "pt";
    if (l === "en") return "en";
    return "es";
};

const DownloadBtn = memo(({ onClick }) => (
    <Button onClick={onClick} size="small" sx={{
        position: "absolute", top: 10, right: 10, minWidth: "auto", p: "10px",
        bgcolor: "rgba(255,255,255,0.92)", border: `1px solid ${DESIGN.border}`,
        borderRadius: DESIGN.radiusSm, color: DESIGN.blue, zIndex: 1,
        "&:hover": { bgcolor: "#fff", borderColor: DESIGN.blue },
        transition: "all 0.2s",
    }}>
        <DownloadIcon sx={{ fontSize: 17 }} />
    </Button>
));

const chartPaperSx = (extra = {}) => ({
    position: "relative", p: 3,
    borderRadius: DESIGN.radius, border: `1px solid ${DESIGN.border}`,
    bgcolor: DESIGN.surface, boxShadow: DESIGN.shadow,
    transition: "all 0.25s ease",
    "&:hover": { boxShadow: DESIGN.shadowHover, transform: "translateY(-2px)", borderColor: DESIGN.borderHover },
    ...extra,
});

const KpiCard = memo(({ label, value, unit, color = DESIGN.blue, subtitle }) => (
    <Box sx={{
        flex: "1 1 140px", minWidth: 120, p: "16px 20px",
        borderRadius: DESIGN.radiusSm, border: `1px solid ${DESIGN.border}`,
        bgcolor: DESIGN.surface, boxShadow: "0 2px 8px rgba(10,15,46,0.06)",
    }}>
        <Typography sx={{ fontSize: "0.94rem", fontWeight: 700, color, letterSpacing: 1, textTransform: "uppercase", mb: 0.5 }}>
            {label}
        </Typography>
        <Typography sx={{ fontSize: "1.7rem", fontWeight: 800, color: DESIGN.text, lineHeight: 1.1 }}>
            {value}
            {unit && <span style={{ fontSize: "0.85rem", fontWeight: 500, color: DESIGN.textSoft, marginLeft: 3 }}>{unit}</span>}
        </Typography>
        {subtitle && <Typography sx={{ fontSize: "0.87rem", color: DESIGN.textSoft, mt: 0.5 }}>{subtitle}</Typography>}
    </Box>
));

const ChartTitle = memo(({ title, subtitle }) => (
    <>
        <Typography sx={{ fontWeight: 700, fontSize: "1.10rem", letterSpacing: -0.2, color: DESIGN.text, mb: 0.5 }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: "0.96rem", color: DESIGN.textSoft, letterSpacing: 0.3, mb: 2.5 }}>{subtitle}</Typography>}
    </>
));

const EmptyState = memo(() => (
    <Box sx={{ mt: 6, textAlign: "center" }}>
        <Box sx={{ width: 56, height: 56, borderRadius: "50%", bgcolor: DESIGN.accentSoft, border: `1px solid ${DESIGN.border}`, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
            <Typography sx={{ fontSize: 24, color: DESIGN.blue, opacity: 0.5 }}>—</Typography>
        </Box>
    </Box>
));

const Dashboard = () => {
    const FECHA_FIJA = useMemo(() => dayjs(), []);
    const { wellId } = useParams();
    const { t, i18n } = useTranslation();
    const { auth } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [isPending, startTransition] = useTransition();

    const lang = useMemo(() => toLang(i18n.language), [i18n.language]);

    const [pozoSeleccionado, setPozoSeleccionado] = useState(wellId || null);
    const [filtroFecha, setFiltroFecha] = useState("14d");
    const [filtroTurno, setFiltroTurno] = useState("todos");
    const [proyectoSeleccionado, setProyectoSeleccionado] = useState("todos");

    const [rawData, setRawData] = useState([]);
    const [pozos, setPozos] = useState([]);
    const [proyectos, setProyectos] = useState([]);
    const [loading, setLoading] = useState(true);

    const refTotales = useRef(null);
    const refPerforados = useRef(null);
    const refHorasEfectivas = useRef(null);
    const refROP = useRef(null);
    const refCurvaAvance = useRef(null);
    const refDistribucionActiv = useRef(null);

    const opcionesFecha = useMemo(() => ({
        "14d": t("dashboard.last14d", "Últimos 14 días"),
        "1m": t("dashboard.last1m", "Último mes"),
        "3m": t("dashboard.last3m", "Último trimestre"),
    }), [t]);

    const opcionesTurno = useMemo(() => [
        { clave: "todos", label: t("dashboard.todos") },
        { clave: "dia", label: t("dashboard.dia") },
        { clave: "noche", label: t("dashboard.noche") },
    ], [t]);

    useEffect(() => {
        startTransition(() => setPozoSeleccionado(wellId || null));
    }, [wellId]);

    useEffect(() => {
        let cancelled = false;
        const fetchAllData = async () => {
            setLoading(true);
            try {
                const dataRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/ocr-results-db`, { withCredentials: true });
                axios.post(`${process.env.REACT_APP_API_URL}/api/viewDashboard`, { pozo: wellId }, { withCredentials: true }).catch(() => { });

                if (cancelled) return;

                const partesDiarios = dataRes.data.filter(doc => (doc.categoria || "partes_diarios") === "partes_diarios");

                const registros = partesDiarios.map((doc) => {
                    const c = doc.campos || {};
                    const fechaISO = parseFechaGlobal(c.fecha);
                    const metrosRaw = parseFloat((c.metros_perf || "").toString().replace(",", ".")) || 0;
                    const hsPerf = parseHsPerf(c.hs_perf);
                    return {
                        fecha: fechaISO,
                        fechaFmt: fechaISO ? dayjs(fechaISO).format("DD/MM/YYYY") : "",
                        proyecto: c.proyecto || "Sin proyecto",
                        metros_perf: metrosRaw,
                        prof_final: parseFloat((c.prof_final || "").toString().replace(",", ".")) || null,
                        hs_perf: hsPerf,
                        turno: (c.turno || "").toLowerCase(),
                        agua: parseFloat(c.agua) || 0,
                        agua_viajes: parseInt(c.agua_viajes) || 0,
                        gasoil: parseFloat(c.gasoil) || 0,
                        nafta: parseFloat(c.nafta) || 0,
                        pozo: c.pozo || "Pozo desconocido",
                        // Actividades ya normalizadas al cargar — no en cada render
                        actividades: (c.desarrollo_trabajo?.actividades || []).map((a) => ({
                            ...a,
                            _cat: normalizeActivity(a.actividad), // { key, color, labels }
                        })),
                    };
                });

                const pozosUnicos = [...new Set(registros.map(r => r.pozo).filter(Boolean))].sort();
                const proyectosUnicos = [...new Set(registros.map(r => r.proyecto))];

                if (!cancelled) {
                    setRawData(registros);
                    setPozos(pozosUnicos);
                    setProyectos(proyectosUnicos);
                    setPozoSeleccionado(prev => {
                        if (prev && pozosUnicos.includes(prev)) return prev;
                        return pozosUnicos[0] || null;
                    });
                }
            } catch (error) {
                console.error("Error al cargar los datos:", error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchAllData();
        return () => { cancelled = true; };
    }, []);

    const handlePozoChange = useCallback((e) => {
        startTransition(() => setPozoSeleccionado(e.target.value));
    }, []);

    const handleFechaChange = useCallback((key) => {
        setFiltroFecha(key);
    }, []);

    const handleTurnoChange = useCallback((e) => {
        startTransition(() => setFiltroTurno(e.target.value));
    }, []);

    const handleProyectoChange = useCallback((e) => {
        startTransition(() => setProyectoSeleccionado(e.target.value));
    }, []);

    const fechaDesde = useMemo(() => {
        if (filtroFecha === "14d") return FECHA_FIJA.subtract(14, "day");
        if (filtroFecha === "1m") return FECHA_FIJA.subtract(1, "month");
        if (filtroFecha === "3m") return FECHA_FIJA.subtract(3, "month");
        return null;
    }, [filtroFecha, FECHA_FIJA]);

    const dataByPozo = useMemo(() => {
        if (!rawData.length) return [];
        return rawData.filter(d =>
            d.fecha &&
            (!pozoSeleccionado || d.pozo === pozoSeleccionado) &&
            (proyectoSeleccionado === "todos" || d.proyecto === proyectoSeleccionado) &&
            (filtroTurno === "todos" || d.turno === filtroTurno)
        );
    }, [rawData, pozoSeleccionado, proyectoSeleccionado, filtroTurno]);

    const dataFiltrada = useMemo(() => {
        if (!dataByPozo.length) return [];
        return dataByPozo.filter(d => {
            const fechaCorte = POZO_FECHAS_CORTE[d.pozo];
            if (fechaCorte) return !dayjs(d.fecha).isAfter(dayjs(fechaCorte), "day");
            return fechaDesde ? dayjs(d.fecha).isSameOrAfter(fechaDesde, "day") : true;
        });
    }, [dataByPozo, fechaDesde]);

    const esMensual = filtroFecha === "3m";

    const claveFor = useCallback(
        (fechaISO) => esMensual ? dayjs(fechaISO).format("MM/YYYY") : dayjs(fechaISO).format("DD/MM/YYYY"),
        [esMensual]
    );

    const sortedEntries = useCallback((mapa) => {
        const fmt = esMensual ? "MM/YYYY" : "DD/MM/YYYY";
        return Object.entries(mapa).sort(([a], [b]) => dayjs(a, fmt).unix() - dayjs(b, fmt).unix());
    }, [esMensual]);

    const totalesAcumulados = useMemo(() => {
        const acc = { partes: 0, metros: 0, gasoil: 0, agua: 0, hsPerf: 0, viajesAgua: 0 };
        for (const d of dataByPozo) {
            acc.partes++;
            acc.metros += d.metros_perf;
            acc.gasoil += d.gasoil;
            acc.agua += d.agua;
            acc.hsPerf += d.hs_perf;
            acc.viajesAgua += d.agua_viajes;
        }
        return acc;
    }, [dataByPozo]);

    const metrosPorDia = useMemo(() => {
        const mapa = {};
        const fechaCortePozo = POZO_FECHAS_CORTE[pozoSeleccionado];
        const fechaTecho = fechaCortePozo ? dayjs(fechaCortePozo) : FECHA_FIJA;
        let fechaInicio;
        if (filtroFecha === "14d") fechaInicio = fechaTecho.subtract(13, "day");
        else if (filtroFecha === "1m") fechaInicio = fechaTecho.subtract(29, "day");
        else if (filtroFecha === "3m") fechaInicio = fechaTecho.subtract(2, "month").startOf("month");

        if (fechaInicio) {
            let cursor = fechaInicio.clone();
            while (!cursor.isAfter(fechaTecho)) {
                const clave = claveFor(cursor.format("YYYY-MM-DD"));
                if (!mapa[clave]) mapa[clave] = { dia: 0, noche: 0 };
                cursor = esMensual ? cursor.add(1, "month") : cursor.add(1, "day");
            }
        }

        for (const { fecha, metros_perf, turno } of dataFiltrada) {
            const clave = claveFor(fecha);
            if (!mapa[clave]) mapa[clave] = { dia: 0, noche: 0 };
            if (turno === "dia") mapa[clave].dia += metros_perf;
            else if (turno === "noche") mapa[clave].noche += metros_perf;
        }

        return sortedEntries(mapa).map(([fecha, v]) => ({
            fecha, dia: +v.dia.toFixed(2), noche: +v.noche.toFixed(2),
        }));
    }, [dataFiltrada, filtroFecha, pozoSeleccionado, esMensual, claveFor, sortedEntries, FECHA_FIJA]);

    const horasEfectivasPorDia = useMemo(() => {
        const mapa = {};
        for (const { fecha, actividades } of dataFiltrada) {
            if (!actividades?.length) continue;
            const clave = claveFor(fecha);
            console.log("actividades", actividades)
            for (const { _cat, cargo } of actividades) {
                // Solo sumamos las filas cuya categoría normalizada sea perforación
                console.log("_cat", _cat)
                if (_cat?.key !== "drilling") continue;

                let hrs = parseFloat(String(cargo || "0").replace(",", ".")) || 0;
                mapa[clave] = (mapa[clave] || 0) + hrs;
            }
        }
        return sortedEntries(mapa).map(([fecha, horas]) => ({ fecha, horas: +horas.toFixed(2) }));
    }, [dataFiltrada, claveFor, sortedEntries]);

    const ropPorDia = useMemo(() => {
        const mapa = {};
        for (const { fecha, metros_perf, actividades } of dataFiltrada) {
            const clave = claveFor(fecha);
            if (!mapa[clave]) mapa[clave] = { metros: 0, horas: 0 };
            mapa[clave].metros += metros_perf;

            for (const { _cat, cargo } of (actividades || [])) {
                if (_cat?.key !== "drilling") continue;
                mapa[clave].horas += parseFloat(String(cargo || "0").replace(",", ".")) || 0;
            }
        }
        return sortedEntries(mapa).map(([fecha, v]) => ({
            fecha, rop: v.horas > 0 ? +(v.metros / v.horas).toFixed(3) : 0,
        }));
    }, [dataFiltrada, claveFor, sortedEntries]);

    const curvaAvance = useMemo(() => {
        return dataFiltrada
            .filter(d => d.prof_final !== null && !isNaN(d.prof_final))
            .sort((a, b) => dayjs(a.fecha).unix() - dayjs(b.fecha).unix())
            .map(d => ({ fecha: d.fechaFmt, prof: d.prof_final }));
    }, [dataFiltrada]);

    const { distribucionActividades, activityCategoriesKey } = useMemo(() => {
        const mapa = {};
        const catKeysUsed = new Set();

        for (const { fecha, actividades } of dataFiltrada) {
            if (!actividades?.length) continue;
            const clave = claveFor(fecha);
            if (!mapa[clave]) mapa[clave] = {};

            for (const { _cat, cargo } of actividades) {
                if (!_cat || !cargo) continue;

                const hrs = parseFloat(String(cargo).replace(",", ".")) || 0;
                if (hrs === 0) continue;

                const key = _cat.key;
                catKeysUsed.add(key);
                mapa[clave][key] = (mapa[clave][key] || 0) + hrs;
            }
        }

        const orderedCatKeys = getAllCategories()
            .map(c => c.key)
            .filter(k => catKeysUsed.has(k));

        const chartData = sortedEntries(mapa).map(([fecha, cats]) => {
            const row = { fecha };
            for (const key of orderedCatKeys) {
                const cat = getAllCategories().find(c => c.key === key);
                const label = cat?.labels[lang] ?? key;
                row[label] = +(cats[key] || 0).toFixed(2);
            }
            return row;
        });

        const translatedKeys = orderedCatKeys.map(key => {
            const cat = getAllCategories().find(c => c.key === key);
            return cat?.labels[lang] ?? key;
        });

        return { distribucionActividades: chartData, activityCategoriesKey: translatedKeys };
    }, [dataFiltrada, claveFor, sortedEntries, lang]);

    const getColorByLabel = useCallback((label) => {
        const cat = getAllCategories().find(c =>
            Object.values(c.labels).includes(label)
        );
        return cat?.color ?? "#90a4ae";
    }, []);

    const descargarGraficoComoImagen = useCallback(async (ref, nombre = "grafico") => {
        if (!ref.current) return;
        const btns = ref.current.querySelectorAll("button");
        btns.forEach(b => b.classList.add("oculto-para-captura"));
        await new Promise(r => setTimeout(r, 100));
        const canvas = await html2canvas(ref.current);
        btns.forEach(b => b.classList.remove("oculto-para-captura"));
        const link = document.createElement("a");
        link.download = `${nombre}.png`;
        link.href = canvas.toDataURL();
        link.click();
    }, []);

    const fmtMetros = useCallback((v, name) => [`${v} m`, name === "dia" ? t("dashboard.dia", "Día") : t("dashboard.noche", "Noche")], [t]);
    const fmtLabel = useCallback((l) => { const d = dayjs(l, "DD/MM/YYYY", true); return d.isValid() ? d.format("DD/MM/YYYY") : l; }, []);
    const fmtHoras = useCallback((v) => [`${v.toFixed(2)} h`, t("dashboard.horas_efectivas_labelNew")], [t]);
    const fmtRop = useCallback((v) => [`${v.toFixed(3)} m/h`, "ROP"], []);
    const fmtProf = useCallback((v) => [`${v} m`, t("dashboard.profundidadNew")], [t]);
    const fmtActiv = useCallback((v, name) => {
        if (!v || v === 0) return [null, null];
        return [`${v.toFixed(2)} h`, name];
    }, []);
    const fmtYHoras = useCallback((v) => `${v.toFixed(0)} h`, []);
    const fmtYProf = useCallback((v) => `${v} m`, []);

    const legendMetros = useMemo(() => [
        { value: t("dashboard.dia", "Día"), type: "square", color: DESIGN.blue },
        { value: t("dashboard.noche", "Noche"), type: "square", color: "#388e3c" },
    ], [t]);

    if (loading) return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
            <CircularProgress />
        </Box>
    );

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, overflowX: "hidden", opacity: isPending ? 0.75 : 1, transition: "opacity 0.12s" }}>

            <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography variant="h4" sx={{
                    fontWeight: 900, color: DESIGN.text, letterSpacing: "-0.05em", mb: 1,
                    px: 1.5, py: 0.5, borderLeft: "6px solid #1976d2",
                    backgroundColor: "rgba(25,118,210,0.08)", borderRadius: "0 8px 8px 0",
                    display: "inline-flex", alignItems: "center",
                }}>
                    {t("dashboard.page_titleNew")}
                </Typography>
            </Box>

            <Box sx={{
                p: "20px 24px", borderRadius: DESIGN.radius,
                border: `1px solid ${DESIGN.border}`, bgcolor: DESIGN.surfaceAlt, mb: 4,
                display: "flex", flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "stretch", md: "center" },
                gap: { xs: 3, md: 0 }, justifyContent: "space-between",
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
                    <Box sx={{
                        display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 0.75,
                        px: 1.5, py: 0.5, bgcolor: DESIGN.blue, borderRadius: "8px", flexShrink: 0,
                    }}>
                        <Box sx={{
                            width: 8, height: 8, borderRadius: "50%", bgcolor: "#00E5FF",
                            boxShadow: "0 0 6px rgba(0,229,255,0.9)",
                            animation: "pulse 2s ease-in-out infinite",
                            "@keyframes pulse": {
                                "0%, 100%": { opacity: 1, transform: "scale(1)" },
                                "50%": { opacity: 0.5, transform: "scale(1.4)" },
                            },
                        }} />
                        <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: "#fff", letterSpacing: 1, textTransform: "uppercase" }}>
                            {t("dashboard.pozo")}
                        </Typography>
                    </Box>

                    <FormControl sx={{
                        minWidth: { xs: "100%", sm: 200 },
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "10px", fontSize: "1rem", fontWeight: 700,
                            height: 52, bgcolor: "#fff", border: `2px solid ${DESIGN.blue}`,
                            "& fieldset": { border: "none" },
                            "&:hover": { bgcolor: "#f0f4ff" },
                            "&.Mui-focused": { boxShadow: `0 0 0 3px rgba(21,101,192,0.18)` },
                        },
                        "& .MuiInputLabel-root": { fontSize: "0.875rem", fontWeight: 600, color: DESIGN.blue },
                        "& .MuiInputLabel-root.Mui-focused": { color: DESIGN.blue },
                        "& .MuiSelect-select": { color: DESIGN.text },
                    }}>
                        <Select value={pozoSeleccionado || ""} onChange={handlePozoChange} displayEmpty>
                            {pozos.length === 0 && (
                                <MenuItem value="" disabled sx={{ fontSize: "0.9rem", fontStyle: "italic", color: DESIGN.textSoft }}>
                                    Cargando pozos…
                                </MenuItem>
                            )}
                            {pozos.map((p) => (
                                <MenuItem key={p} value={p} sx={{ fontSize: "1.05rem", fontWeight: 600 }}>{p}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ display: { xs: "none", md: "block" }, width: "1px", height: 44, bgcolor: DESIGN.border, mx: 3, flexShrink: 0 }} />

                <Box sx={{
                    display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center",
                    justifyContent: { xs: "flex-start", md: "flex-end" }, flex: 1,
                }}>
                    <FormControl sx={{
                        minWidth: 140,
                        "& .MuiOutlinedInput-root": {
                            borderRadius: DESIGN.radiusSm, fontSize: "0.87rem", height: 40, bgcolor: "#fff",
                            "& fieldset": { borderColor: DESIGN.border },
                            "&:hover fieldset": { borderColor: DESIGN.blue },
                        },
                        "& .MuiInputLabel-root": { fontSize: "0.87rem" },
                    }}>
                        <InputLabel>{t("dashboard.turno", "Turno")}</InputLabel>
                        <Select value={filtroTurno} label={t("dashboard.turno", "Turno")} onChange={handleTurnoChange} size="small">
                            {opcionesTurno.map(({ clave, label }) => (
                                <MenuItem key={clave} value={clave} sx={{ fontSize: "0.87rem" }}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{
                        minWidth: 160,
                        "& .MuiOutlinedInput-root": {
                            borderRadius: DESIGN.radiusSm, fontSize: "0.87rem", height: 40, bgcolor: "#fff",
                            "& fieldset": { borderColor: DESIGN.border },
                            "&:hover fieldset": { borderColor: DESIGN.blue },
                        },
                        "& .MuiInputLabel-root": { fontSize: "0.87rem" },
                    }}>
                        <InputLabel>{t("dashboard.proyecto")}</InputLabel>
                        <Select value={proyectoSeleccionado} label={t("dashboard.proyecto")} onChange={handleProyectoChange} size="small">
                            <MenuItem value="todos" sx={{ fontSize: "0.87rem" }}>{t("dashboard.todos")}</MenuItem>
                            {proyectos.map((p) => (
                                <MenuItem key={p} value={p} sx={{ fontSize: "0.87rem" }}>{p}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ display: { xs: "none", sm: "block" }, width: "1px", height: 28, bgcolor: DESIGN.border }} />

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {Object.entries(opcionesFecha).map(([key, label]) => (
                            <Button key={key}
                                variant={filtroFecha === key ? "contained" : "outlined"}
                                onClick={() => handleFechaChange(key)}
                                sx={{
                                    minWidth: 80, fontWeight: filtroFecha === key ? 700 : 500,
                                    fontSize: "0.88rem", textTransform: "none",
                                    borderRadius: DESIGN.radiusSm, height: 40,
                                    border: filtroFecha === key ? "none" : `1px solid ${DESIGN.border}`,
                                    bgcolor: filtroFecha === key ? DESIGN.blue : "#fff",
                                    color: filtroFecha === key ? "#fff" : DESIGN.textMid,
                                    "&:hover": {
                                        borderColor: DESIGN.blue,
                                        bgcolor: filtroFecha === key ? DESIGN.blueBright : "#f0f4ff",
                                    },
                                }}
                            >
                                {label}
                            </Button>
                        ))}
                    </Box>
                </Box>
            </Box>

            {dataFiltrada.length === 0 ? (
                <Box sx={{ mt: 8, textAlign: "center" }}>
                    <Box sx={{ width: 64, height: 64, borderRadius: "50%", bgcolor: DESIGN.accentSoft, border: `1px solid ${DESIGN.border}`, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
                        <Typography sx={{ fontSize: 28, color: DESIGN.blue, opacity: 0.5 }}>—</Typography>
                    </Box>
                    <Typography sx={{ color: DESIGN.textSoft, fontSize: "0.875rem" }}>{t("dashboard.noData")}</Typography>
                </Box>
            ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "center" }}>

                    <Paper ref={refTotales} elevation={0} sx={chartPaperSx({ width: "100%" })}>
                        <DownloadBtn onClick={() => descargarGraficoComoImagen(refTotales, "totales_acumulados")} />
                        <ChartTitle title={t("dashboard.totales_acumuladosNew")} subtitle={t("dashboard.totales_acumulados_subtitleNew")} />
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
                            <KpiCard label={t("dashboard.partes_registradosNew")} value={totalesAcumulados.partes} subtitle={t("dashboard.turnos_cargados", "Turnos cargados")} color="#ffa726" />
                            <KpiCard label={t("dashboard.metros_perforados_total", "Metros perforados")} value={totalesAcumulados.metros.toFixed(2)} unit="m" subtitle={t("dashboard.avance_total", "Avance total")} color="#388e3c" />
                            <KpiCard label={t("dashboard.gasoil_consumido", "Gasoil consumido")} value={totalesAcumulados.gasoil.toLocaleString()} unit="l" subtitle={t("dashboard.suma_consumo", "Suma del consumo")} color="#ef5350" />
                            <KpiCard label={t("dashboard.agua_salada_usada", "Agua salada")} value={totalesAcumulados.agua.toLocaleString()} unit="l" subtitle={t("dashboard.total_abastecido", "Total abastecido")} color="#42a5f5" />
                            <KpiCard label={t("dashboard.horas_perforacionNew", "Hs. perforación")} value={formatHoras(totalesAcumulados.hsPerf)} subtitle={t("dashboard.tiempo_efectivo_acum", "Tiempo efectivo")} color={DESIGN.blue} />
                            {totalesAcumulados.viajesAgua > 0 && (
                                <KpiCard label={t("dashboard.viajes_agua", "Viajes de agua")} value={totalesAcumulados.viajesAgua} subtitle={t("dashboard.camiones_proyecto", "Camiones")} color="#7e57c2" />
                            )}
                        </Box>
                    </Paper>

                    <Paper ref={refPerforados} elevation={0} sx={chartPaperSx({ width: "100%" })}>
                        <DownloadBtn onClick={() => descargarGraficoComoImagen(refPerforados, "metros_perforados")} />
                        <ChartTitle title={t("dashboard.total_metros")} subtitle={t("dashboard.metros_perforados_subtitleNew")} />
                        {metrosPorDia.length === 0 ? <EmptyState /> : (
                            <ResponsiveContainer width="100%" height={350}>
                                <LineChart data={metrosPorDia} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={DESIGN.border} />
                                    <XAxis dataKey="fecha" angle={-45} textAnchor="end" tick={{ fontSize: 14, fill: DESIGN.textSoft }} interval={0} height={60} axisLine={{ stroke: DESIGN.border }} tickLine={false} tickFormatter={tickFmt} />
                                    <YAxis tick={{ fontSize: 11, fill: DESIGN.textSoft }} axisLine={false} tickLine={false} label={{ value: "m", angle: -90, position: "insideLeft", dx: -10, style: { textAnchor: "middle", fontSize: 12, fill: DESIGN.textSoft } }} />
                                    <Tooltip {...TOOLTIP_STYLE} formatter={fmtMetros} labelFormatter={fmtLabel} />
                                    <Legend payload={legendMetros} />
                                    <Line type="monotone" dataKey="dia" stroke={DESIGN.blue} strokeWidth={2.5} dot={{ r: 3, fill: DESIGN.blue, strokeWidth: 0 }} activeDot={{ r: 6, fill: DESIGN.blueBright, stroke: "#fff", strokeWidth: 2 }} name="dia" isAnimationActive={false} />
                                    <Line type="monotone" dataKey="noche" stroke="#388e3c" strokeWidth={2.5} dot={{ r: 3, fill: "#388e3c", strokeWidth: 0 }} activeDot={{ r: 6, fill: "#4caf50", stroke: "#fff", strokeWidth: 2 }} name="noche" isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>

                    <Paper ref={refHorasEfectivas} elevation={0} sx={chartPaperSx({ width: "100%" })}>
                        <DownloadBtn onClick={() => descargarGraficoComoImagen(refHorasEfectivas, "horas_efectivas")} />
                        <ChartTitle title={t("dashboard.horas_efectivas_tituloNew")} subtitle={t("dashboard.horas_efectivas_subtitleNew")} />
                        {horasEfectivasPorDia.length === 0 ? <EmptyState /> : (
                            <ResponsiveContainer width="100%" height={320}>
                                <LineChart data={horasEfectivasPorDia} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={DESIGN.border} />
                                    <XAxis dataKey="fecha" angle={-45} textAnchor="end" tick={{ fontSize: 11, fill: DESIGN.textSoft }} interval={0} height={60} axisLine={{ stroke: DESIGN.border }} tickLine={false} tickFormatter={tickFmt} />
                                    <YAxis tick={{ fontSize: 11, fill: DESIGN.textSoft }} axisLine={false} tickLine={false} domain={[0, 12]} label={{ value: "h", angle: -90, position: "insideLeft", dx: -10, style: { textAnchor: "middle", fontSize: 12, fill: DESIGN.textSoft } }} />
                                    <Tooltip {...TOOLTIP_STYLE} formatter={fmtHoras} />
                                    <ReferenceLine y={7} stroke="#b2dfdb" strokeDasharray="4 4" label={{ value: "7hs ref.", fill: "#4caf50", fontSize: 14 }} />
                                    <Line type="monotone" dataKey="horas" stroke="#43a047" strokeWidth={2.5} dot={{ r: 4, fill: "#43a047", strokeWidth: 0 }} activeDot={{ r: 6, fill: "#66bb6a", stroke: "#fff", strokeWidth: 2 }} isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>

                    <Paper ref={refROP} elevation={0} sx={chartPaperSx({ width: "100%" })}>
                        <DownloadBtn onClick={() => descargarGraficoComoImagen(refROP, "rop")} />
                        <ChartTitle title={t("dashboard.rop_tituloNew")} subtitle={t("dashboard.rop_subtitleNew")} />
                        {ropPorDia.length === 0 ? <EmptyState /> : (
                            <ResponsiveContainer width="100%" height={320}>
                                <LineChart data={ropPorDia} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={DESIGN.border} />
                                    <XAxis dataKey="fecha" angle={-45} textAnchor="end" tick={{ fontSize: 11, fill: DESIGN.textSoft }} interval={0} height={60} axisLine={{ stroke: DESIGN.border }} tickLine={false} tickFormatter={tickFmt} />
                                    <YAxis tick={{ fontSize: 11, fill: DESIGN.textSoft }} axisLine={false} tickLine={false} label={{ value: "m/h", angle: -90, position: "insideLeft", dx: -10, style: { textAnchor: "middle", fontSize: 12, fill: DESIGN.textSoft } }} />
                                    <Tooltip {...TOOLTIP_STYLE} formatter={fmtRop} />
                                    <Line type="monotone" dataKey="rop" stroke="#9c27b0" strokeWidth={2.5} dot={{ r: 4, fill: "#9c27b0", strokeWidth: 0 }} activeDot={{ r: 6, fill: "#ba68c8", stroke: "#fff", strokeWidth: 2 }} isAnimationActive={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>

                    {curvaAvance.length > 0 && (
                        <Paper ref={refCurvaAvance} elevation={0} sx={chartPaperSx({ width: "100%" })}>
                            <DownloadBtn onClick={() => descargarGraficoComoImagen(refCurvaAvance, "curva_avance")} />
                            <ChartTitle title={t("dashboard.curva_avance_tituloNew")} subtitle={t("dashboard.curva_avance_subtitleNew")} />
                            <ResponsiveContainer width="100%" height={340}>
                                <AreaChart data={curvaAvance} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                                    <defs>
                                        <linearGradient id="gradAvance" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#ffa726" stopOpacity={0.25} />
                                            <stop offset="95%" stopColor="#ffa726" stopOpacity={0.02} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={DESIGN.border} />
                                    <XAxis dataKey="fecha" angle={-45} textAnchor="end" tick={{ fontSize: 11, fill: DESIGN.textSoft }} interval={0} height={60} axisLine={{ stroke: DESIGN.border }} tickLine={false} tickFormatter={tickFmt} />
                                    <YAxis reversed tick={{ fontSize: 11, fill: DESIGN.textSoft }} axisLine={false} tickLine={false} tickFormatter={fmtYProf} label={{ value: "Prof. (m)", angle: -90, position: "insideLeft", dx: -10, style: { textAnchor: "middle", fontSize: 12, fill: DESIGN.textSoft } }} />
                                    <Tooltip {...TOOLTIP_STYLE} formatter={fmtProf} />
                                    <Area type="monotone" dataKey="prof" stroke="#e65100" strokeWidth={2.5} fill="url(#gradAvance)" dot={{ r: 4, fill: "#e65100", strokeWidth: 0 }} activeDot={{ r: 6, fill: "#ff7043", stroke: "#fff", strokeWidth: 2 }} isAnimationActive={false} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </Paper>
                    )}

                    {distribucionActividades.length > 0 && activityCategoriesKey.length > 0 && (
                        <Paper ref={refDistribucionActiv} elevation={0} sx={chartPaperSx({ width: "100%" })}>
                            <DownloadBtn onClick={() => descargarGraficoComoImagen(refDistribucionActiv, "distribucion_actividades")} />
                            <ChartTitle title={t("dashboard.distribucion_activ_tituloNew")} subtitle={t("dashboard.distribucion_activ_subtitleNew")} />
                            <ResponsiveContainer width="100%" height={420}>
                                <BarChart data={distribucionActividades} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={DESIGN.border} />
                                    <XAxis dataKey="fecha" angle={-45} textAnchor="end" tick={{ fontSize: 11, fill: DESIGN.textSoft }} interval={0} height={60} axisLine={{ stroke: DESIGN.border }} tickLine={false} tickFormatter={tickFmt} />
                                    <YAxis tick={{ fontSize: 11, fill: DESIGN.textSoft }} axisLine={false} tickLine={false} tickFormatter={fmtYHoras} label={{ value: "h", angle: -90, position: "insideLeft", dx: -10, style: { textAnchor: "middle", fontSize: 12, fill: DESIGN.textSoft } }} />
                                    <Tooltip content={<CustomTooltipActiv />} />
                                    <Legend wrapperStyle={{ paddingTop: 12, fontSize: 12, color: DESIGN.textMid }} />
                                    {activityCategoriesKey.map((label, idx) => (
                                        <Bar
                                            key={label}
                                            dataKey={label}
                                            stackId="a"
                                            fill={getColorByLabel(label)}
                                            isAnimationActive={false}
                                            radius={idx === activityCategoriesKey.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                                        />
                                    ))}
                                </BarChart>
                            </ResponsiveContainer>
                        </Paper>
                    )}

                </Box>
            )}
        </Box>
    );
};

export default Dashboard;